import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { Injectable, NgModule, provideZonelessChangeDetection } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { StoreModule } from '@ngrx/store';

import { AnalyticsSegmentClient, RtAnalyticsModule } from '@core/analytics-legacy';
import { AnalyticsClient } from '@core/analytics-types';
import { resolveLayoutAsset } from '@core/assets-logic/theme-layouts/resolver';
import {
  AccountBase,
  APP_CONFIG,
  DetachedRouterOutlet,
  RtcErrorHandlerModule,
} from '@core/common-legacy';
import { provideLayoutAssetsResolver } from '@core/components-theme-picker-legacy';
import { CountryDataModule, injectCountryList } from '@core/country-data';
import { I18nModule, LANGUAGE_CONFIG, LOCALE } from '@core/i18n-legacy';
import {
  IconsContainerBundledStrategy,
  IconsContainerComponent,
  PHONE_INPUT_DATA,
  protectedControlsRootProviders,
  provideIconsContainerStrategy,
} from '@core/user-interface-legacy';

import { AccountSetupRoutingModule } from './account-setup.routing.module';
import { AppComponent } from './app.component';
import { AppRevisionProvider, AppRevisionToken } from './app.revision';
import { environment } from '../environments/environment';

export const defaultLocale = 'en';

@Injectable()
export class Account extends AccountBase {}

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    ReactiveFormsModule.withConfig({ callSetDisabledState: 'whenDisabledForLegacyCode' }),
    RtAnalyticsModule.forRoot({
      disable: !environment.analytics?.isEnabled,
      key: environment.analytics?.key || '',
      trackRouteChange: true,
    }),
    BrowserModule,
    I18nModule.forPhraseApp({
      phraseAppProjectId: environment.i18n.phraseAppProjectId,
      editorEnabled: environment.i18n.editorEnabled,
    }),
    RtcErrorHandlerModule.forRoot(
      {
        enable: environment.angularProdMode,
        dsn: environment.sentry.dsn,
        environmentName: environment.sentry.environmentName,
        revisionToken: AppRevisionToken,
      },
      Account,
    ),
    AccountSetupRoutingModule,
    StoreModule.forRoot({}),
    CountryDataModule,
    BrowserAnimationsModule,
    IconsContainerComponent,
    DetachedRouterOutlet,
  ],
  providers: [
    Account,
    AppRevisionProvider,
    protectedControlsRootProviders(),
    { provide: LOCALE, useValue: defaultLocale },
    {
      provide: APP_CONFIG,
      useValue: {
        isE2e: !!window.Cypress,
      },
    },
    {
      provide: LANGUAGE_CONFIG,
      useValue: {
        defaultLanguage: 'en',
        languages: ['en', 'nl', 'de', 'fr', 'sv', 'fi'],
      },
    },
    {
      provide: PHONE_INPUT_DATA,
      useFactory: injectCountryList,
    },
    provideLayoutAssetsResolver(resolveLayoutAsset),
    provideHttpClient(withInterceptorsFromDi()),
    provideZonelessChangeDetection(),
    { provide: AnalyticsClient, useExisting: AnalyticsSegmentClient },
    provideIconsContainerStrategy({ useClass: IconsContainerBundledStrategy }),
  ],
})
export class AppModule {}
